// React & Hook imports
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

// Third-party library imports
import { Form, Field, useFormState, useForm } from 'react-final-form';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import Divider from '@mui/material/Divider';

// Component imports
import MultiLineTextAreaField from 'components/MultiLineTextAreaField';
import PrimaryCta from 'components/Buttons/PrimaryCta';
import QuantityInput from 'components/QuantityInput';
import TextInputField from 'components/TextInputField';
import MenuItemDescription from 'components/Menu/MenuItemDescription';
import MenuItemImage from 'components/Menu/MenuItemImage';
import OutlinedBoxError from 'components/OutlinedBoxError';
import MenuItemDetailPrice from 'components/Menu/MenuItemDetailPrice';
import MenuItemDescriptionTypography from 'components/MenuItemDescriptionTypography';
import StyledVariantBox from 'components/StyledVariantBox';
import StyledMenuDetailsModalBoxWrapper from 'components/StyledMenuDetailsModalBoxWrapper';
import ProductVariants from 'catering/components/Forms/MenuItemDetailsForm/ProductVariants';
import SpecialOrderInquiryMsg from 'catering/components/Forms/MenuItemDetailsForm/SpecialOrderInquiryMsg';
import VariantGroups from 'catering/components/Forms/MenuItemDetailsForm/VariantGroups';

// Constants & helpers imports
import { ADD_TO_CART, UPDATE_ITEM } from 'constants/menu/ctaName';
import { CART_ITEM, MENU_ITEM } from 'constants/menu/formTypes';
import {
  ORDER_MADE_FOR_LENGTH,
  SPECIAL_INSTRUCTIONS_LENGTH
} from 'constants/menu/inputSize';
import { OPACITY_13 } from 'constants/opacity';
import {
  getAddToCartLabel,
  getAreRequiredFieldsSelected
} from 'helpers/forms/addToCartCta';
import { calculateRgbaWithAlpha } from 'helpers/colorFormatter';
import { updateQuantityToMin } from 'catering/helpers/menu';
import { getQuantityOfProductInCart } from 'bento-ordering/cart/selectors';
import {
  getInventoryIsDepletedWithCurrentQuantity,
  doesPreviewedQuantityExceedsInventory,
  getCartItemById
} from 'catering/helpers/productInventory';
import { ITEM_DESCRIPTION_AND_PRICES } from 'styles/constants/appearanceSectionNames';
import { FIXED } from 'catering/constants/quantityHandlingMethod';

// Hooks & Context imports
import {
  useMenuItemPricePreview,
  useRemoveCartItem
} from 'catering/hooks/query';
import { useAccount } from 'catering/context/providers/AccountProvider';
import useProductHasMultipleSizes from 'hooks/useProductHasMultipleSizes';
import useProductHasSurchargeModifier from 'hooks/useProductHasSurchargeModifier';
import { useGoToMenuScreen } from 'catering/hooks/navigation';

const MinQuantityText = styled(MenuItemDescriptionTypography)({
  marginLeft: '8px'
});

const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: calculateRgbaWithAlpha(
    theme.palette.sectionNav.link.main,
    OPACITY_13
  )
}));

function MenuItemDetailsFormContents({
  cart,
  cateringLocation,
  formType,
  FooterWrapperComponent,
  menuItemDetails,
  isAddingToCart,
  modifierGroups
}) {
  const { cartItemId } = useParams();
  const cartItem = getCartItemById(cart, parseInt(cartItemId));
  const form = useForm();
  const { values: formState } = useFormState();
  const productHasMultipleSizes = useProductHasMultipleSizes(menuItemDetails);
  const productHasSurchargeModifier = useProductHasSurchargeModifier(
    menuItemDetails
  );
  const { name: restaurantName } = useAccount();

  const {
    id,
    images,
    name,
    description,
    default_price,
    quantity_handling_method,
    minimum_quantity,
    variants,
    // DIETARY + ALLERGEN PROPS
    contains_sugar,
    cooked_to_order,
    corn,
    corn_free,
    dairy,
    dairy_free,
    dairy_free_option,
    egg,
    egg_free,
    fish,
    fish_free,
    gluten,
    gluten_free,
    halal,
    keto,
    keto_option,
    kosher,
    kosher_option,
    low_fodmap,
    low_sodium,
    non_alcoholic,
    nuts,
    nuts_free,
    organic,
    organic_option,
    paleo,
    paleo_option,
    peanut,
    peanut_free,
    plant_based,
    plant_based_option,
    raw,
    raw_egg,
    sesame,
    sesame_free,
    shellfish,
    shellfish_free,
    soy,
    soy_free,
    spicy,
    spicy_hot,
    spicy_medium,
    spicy_mild,
    sugar_free,
    tree_nut,
    tree_nut_free,
    uncooked,
    undercooked,
    vegan,
    vegan_option,
    vegetarian,
    vegetarian_option,
    wheat,
    wheat_free
  } = menuItemDetails;

  const shouldRenderProductVariantsSection = productHasMultipleSizes;

  const minQtyStyles = { variant: ITEM_DESCRIPTION_AND_PRICES };

  const menuItemImage = images[0];

  const hasImage = !!menuItemImage?.url;
  const topPadding = hasImage ? 0 : 2.5;

  const { max_order_amount, subtotal } = cart;
  const {
    add_made_for_field: displayOrderMadeForField,
    show_special_instructions: displaySpecialInstructionsField,
    special_order_inquiry_enabled: specialOrderInquiryEnabled,
    special_order_inquiry_email: specialOrderInquiryEmail,
    location: { name: locationName }
  } = cateringLocation;

  const displayQuantityInput = quantity_handling_method !== FIXED;
  const {
    price_preview: menuItemPricePreview,
    inventory,
    lowInventoryCount
  } = useMenuItemPricePreview(menuItemDetails, formState, modifierGroups);

  const isSoldOut = inventory !== null && inventory <= 0;

  const qtyOfProductInCart = getQuantityOfProductInCart(cart, id);

  const inventoryLessCart =
    inventory !== null ? inventory - qtyOfProductInCart : null;

  const inventoryIsDepletedWithCurrentQuantity = getInventoryIsDepletedWithCurrentQuantity(
    inventoryLessCart,
    formState,
    formType,
    cartItem
  );

  const previewedQuantityExceedsInventory = doesPreviewedQuantityExceedsInventory(
    inventory,
    inventoryLessCart,
    formType,
    formState.quantity,
    cart.quantity
  );

  const remainingInventoryIsInCart =
    qtyOfProductInCart > 0 && qtyOfProductInCart === inventory;

  const isOrderMaximumExceeded = max_order_amount
    ? max_order_amount < parseFloat(subtotal) + parseFloat(menuItemPricePreview)
    : false;

  const isMinQtyThresholdReached =
    minimum_quantity && quantity_handling_method
      ? (formState.quantity ?? 1) >= minimum_quantity
      : true;

  // TODO: implement when PATCHING cart. These values will come from the query mutation
  const requestError = null;

  const areRequiredFieldsSelected = getAreRequiredFieldsSelected(
    formState,
    modifierGroups,
    shouldRenderProductVariantsSection
  );

  const shouldRenderDescriptionBorder =
    modifierGroups.length ||
    displayOrderMadeForField ||
    displaySpecialInstructionsField;

  const defaultSubmitLabel = formType === MENU_ITEM ? ADD_TO_CART : UPDATE_ITEM;
  const submitLabel = getAddToCartLabel(
    areRequiredFieldsSelected,
    isAddingToCart,
    defaultSubmitLabel,
    isSoldOut
  );

  const isFormValid =
    areRequiredFieldsSelected &&
    !isOrderMaximumExceeded &&
    isMinQtyThresholdReached &&
    !isAddingToCart &&
    !requestError;

  const selectedQtyEqualsMinQty = formState.quantity === minimum_quantity;

  const [initalQuantitySelected] = useState(formState.quantity);
  const lowInventoryMessageQuantity =
    formType === CART_ITEM
      ? inventoryLessCart + initalQuantitySelected - formState.quantity
      : inventoryLessCart - formState.quantity;

  const shouldDisplayInventoryLeft =
    !!inventoryLessCart &&
    inventoryLessCart > 0 &&
    inventoryLessCart < lowInventoryCount &&
    !previewedQuantityExceedsInventory &&
    lowInventoryMessageQuantity > 0;

  const shouldRenderSpecialOrderInquiryMsg =
    (isSoldOut || inventoryIsDepletedWithCurrentQuantity) &&
    specialOrderInquiryEnabled &&
    specialOrderInquiryEmail;

  const shouldDisplayAddableInventory =
    !shouldRenderSpecialOrderInquiryMsg &&
    !!inventoryLessCart &&
    inventoryLessCart > 0 &&
    !requestError &&
    (inventoryIsDepletedWithCurrentQuantity ||
      previewedQuantityExceedsInventory);

  const shouldDisableDecrementQtyButton =
    formState.quantity === 1 || selectedQtyEqualsMinQty || isSoldOut;

  const shouldDisableIncrementQtyButton =
    isOrderMaximumExceeded ||
    isSoldOut ||
    inventoryIsDepletedWithCurrentQuantity ||
    previewedQuantityExceedsInventory;

  const shouldDisablePrimaryCTA =
    !isFormValid ||
    isSoldOut ||
    previewedQuantityExceedsInventory ||
    (remainingInventoryIsInCart && formType === MENU_ITEM);

  const shouldDisableVariantFields =
    isSoldOut || (remainingInventoryIsInCart && formType === MENU_ITEM);

  const renderSpecialOrderInquiryMsg = () => {
    const specialOrderInquiryMsg = (
      <SpecialOrderInquiryMsg
        isSoldOut={isSoldOut}
        mailTo={specialOrderInquiryEmail}
        itemName={name}
        restaurantName={restaurantName}
        locationName={locationName}
        inventoryLessCart={inventoryLessCart}
      />
    );

    return (
      <Box data-testid="cart-item-special-order-inquiry-box" mb={2.5}>
        <OutlinedBoxError useErrorStyle={false}>
          {specialOrderInquiryMsg}
        </OutlinedBoxError>
      </Box>
    );
  };

  useEffect(() => {
    const isNotFixed = quantity_handling_method !== FIXED;

    const hasMinQuantity = isNotFixed && minimum_quantity > 0;
    const selectedQuantity = formState.quantity;
    // check if the user has selected a quantity so the value is saved when they edit the cart item
    if (selectedQuantity) {
      if (hasMinQuantity && formState.quantity < minimum_quantity) {
        updateQuantityToMin(form, minimum_quantity);
      }
    } else {
      if (hasMinQuantity) {
        updateQuantityToMin(form, minimum_quantity);
      }
    }
  }, [minimum_quantity, form, formState.quantity, quantity_handling_method]);

  const goToMenuScreen = useGoToMenuScreen();
  const removeCateringCartItem = useRemoveCartItem();
  const handleRemoveCartItem = cartItemId => {
    removeCateringCartItem.mutate(cartItemId, {
      onSuccess: goToMenuScreen()
    });
  };

  const removeItemFromCart = () => {
    if (!cartItemId) {
      goToMenuScreen();
    } else {
      handleRemoveCartItem(cartItemId);
    }
  };

  return (
    <StyledMenuDetailsModalBoxWrapper>
      {hasImage && (
        <MenuItemImage
          url={menuItemImage.url}
          focalPointX={menuItemImage.focal_point_x}
          focalPointY={menuItemImage.focal_point_y}
        />
      )}
      <Box paddingTop={topPadding}>
        <MenuItemDescription
          name={name}
          description={description}
          default_price={default_price}
          quantity_handling_method={quantity_handling_method}
          shouldRenderDescriptionBorder={shouldRenderDescriptionBorder}
          minimumQuantity={minimum_quantity}
          productHasMultipleSizes={productHasMultipleSizes}
          productHasSurchargeModifier={productHasSurchargeModifier}
          // DIETARY + ALLERGEN PROPS
          contains_sugar={contains_sugar}
          cooked_to_order={cooked_to_order}
          corn={corn}
          corn_free={corn_free}
          dairy={dairy}
          dairy_free={dairy_free}
          dairy_free_option={dairy_free_option}
          egg={egg}
          egg_free={egg_free}
          fish={fish}
          fish_free={fish_free}
          gluten={gluten}
          gluten_free={gluten_free}
          halal={halal}
          keto={keto}
          keto_option={keto_option}
          kosher={kosher}
          kosher_option={kosher_option}
          low_fodmap={low_fodmap}
          low_sodium={low_sodium}
          non_alcoholic={non_alcoholic}
          nuts={nuts}
          nuts_free={nuts_free}
          organic={organic}
          organic_option={organic_option}
          paleo={paleo}
          paleo_option={paleo_option}
          peanut={peanut}
          peanut_free={peanut_free}
          plant_based={plant_based}
          plant_based_option={plant_based_option}
          raw={raw}
          raw_egg={raw_egg}
          sesame={sesame}
          sesame_free={sesame_free}
          shellfish={shellfish}
          shellfish_free={shellfish_free}
          soy={soy}
          soy_free={soy_free}
          spicy={spicy}
          spicy_hot={spicy_hot}
          spicy_medium={spicy_medium}
          spicy_mild={spicy_mild}
          sugar_free={sugar_free}
          tree_nut={tree_nut}
          tree_nut_free={tree_nut_free}
          uncooked={uncooked}
          undercooked={undercooked}
          vegan={vegan}
          vegan_option={vegan_option}
          vegetarian={vegetarian}
          vegetarian_option={vegetarian_option}
          wheat={wheat}
          wheat_free={wheat_free}
        />
      </Box>

      <Box paddingLeft={2.5} paddingRight={2.5} paddingTop={0}>
        {shouldRenderProductVariantsSection && (
          <>
            <ProductVariants
              variants={variants}
              formState={formState}
              disabled={shouldDisableVariantFields}
            />
            {(!!modifierGroups.length ||
              displaySpecialInstructionsField ||
              displayOrderMadeForField) && <StyledDivider />}
          </>
        )}

        <VariantGroups
          modifierGroups={modifierGroups}
          formState={formState}
          shouldRenderLastBottomBorder={
            displaySpecialInstructionsField || displayOrderMadeForField
          }
          disabled={shouldDisableVariantFields}
        />

        <Box
          width="100%"
          color="borders.primary"
          marginBottom={!displaySpecialInstructionsField ? 2.5 : 0}
        >
          {displayOrderMadeForField && (
            <Field
              name="order-made-for"
              placeholder="Order made for"
              dataTestId="menu-item-detail-order-made-for"
              disabled={isSoldOut}
              component={TextInputField}
              maxLength={ORDER_MADE_FOR_LENGTH}
            />
          )}

          {displaySpecialInstructionsField && (
            <Field
              name="special-instructions"
              placeholder="Special Instructions"
              dataTestId="menu-item-detail-special-instructions"
              disabled={isSoldOut}
              component={MultiLineTextAreaField}
              maxLength={SPECIAL_INSTRUCTIONS_LENGTH}
            />
          )}
        </Box>
      </Box>

      <FooterWrapperComponent>
        <StyledVariantBox
          display="flex"
          marginTop={2.5}
          marginBottom={2.5}
          alignItems="center"
        >
          {displayQuantityInput && (
            <Field name="quantity">
              {({ input }) => (
                <>
                  <QuantityInput
                    disableDecrement={shouldDisableDecrementQtyButton}
                    disableIncrement={shouldDisableIncrementQtyButton}
                    cartItemId={cartItemId}
                    removeItemFromCart={removeItemFromCart}
                    label="Qty:"
                    onChange={event => input.onChange(event.target.value)}
                    value={input.value || 1}
                  />
                  {selectedQtyEqualsMinQty && formState.quantity !== 1 && (
                    <MinQuantityText
                      data-testid="min-quantity"
                      {...minQtyStyles}
                    >{`Min. ${minimum_quantity}`}</MinQuantityText>
                  )}
                  {remainingInventoryIsInCart && (
                    <Box ml={1}>
                      <MenuItemDescriptionTypography
                        variant={ITEM_DESCRIPTION_AND_PRICES}
                        data-testid="menu-item-detail-remaining-inventory-in-cart"
                      >{`Last ${qtyOfProductInCart} in cart`}</MenuItemDescriptionTypography>
                    </Box>
                  )}
                  {shouldDisplayInventoryLeft && (
                    <Box ml={1} sx={{ 'padding-top': '0.75rem' }}>
                      <MenuItemDescriptionTypography
                        data-testid="menu-item-detail-inventory-left"
                        variant={ITEM_DESCRIPTION_AND_PRICES}
                      >{`Only ${lowInventoryMessageQuantity} left`}</MenuItemDescriptionTypography>
                    </Box>
                  )}
                </>
              )}
            </Field>
          )}

          <MenuItemDetailPrice cost={menuItemPricePreview} />
        </StyledVariantBox>
        {isOrderMaximumExceeded && (
          <Box marginBottom={2.5}>
            <Typography variant="caption">
              {`You’ve reached the order maximum of $${max_order_amount}. Please
                    remove items from your cart or adjust their quantities to
                    continue to checkout.`}
            </Typography>
          </Box>
        )}
        {shouldDisplayAddableInventory && (
          <Box mb={2.5}>
            <MenuItemDescriptionTypography
              data-testid="menu-item-detail-limited-quantity-caption"
              variant="caption"
            >
              {`We have a limited quantity of ${name} and only ${inventoryLessCart} can be added to your cart.`}
            </MenuItemDescriptionTypography>
          </Box>
        )}

        {requestError && (
          <Box marginBottom={2.5}>
            <Typography variant="caption">{requestError}</Typography>
          </Box>
        )}
        {shouldRenderSpecialOrderInquiryMsg && renderSpecialOrderInquiryMsg()}
        <PrimaryCta
          fullWidth
          type="submit"
          dataTestId="menu-item-detail-submit-button"
          label={submitLabel}
          disabled={shouldDisablePrimaryCTA}
        />
      </FooterWrapperComponent>
    </StyledMenuDetailsModalBoxWrapper>
  );
}

MenuItemDetailsFormContents.propTypes = {
  cart: PropTypes.object.isRequired,
  cateringLocation: PropTypes.object.isRequired,
  formType: PropTypes.oneOf([CART_ITEM, MENU_ITEM]).isRequired,
  FooterWrapperComponent: PropTypes.elementType.isRequired,
  menuItemDetails: PropTypes.object.isRequired,
  isAddingToCart: PropTypes.bool.isRequired,
  modifierGroups: PropTypes.array.isRequired
};

function MenuItemDetailsForm({ onSubmit, initialValues, ...rest }) {
  return (
    <Form onSubmit={onSubmit} initialValues={initialValues}>
      {({ handleSubmit }) => (
        <form aria-label="form" onSubmit={handleSubmit}>
          <MenuItemDetailsFormContents {...rest} />
        </form>
      )}
    </Form>
  );
}

MenuItemDetailsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default MenuItemDetailsForm;
